import { Suspense, lazy, useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Sidebar from './Sidebar';
import Topbar from './Topbar';
import OfflineBanner from './OfflineBanner';
import ImportHandoffBanner from './ImportHandoffBanner';
import ModuleTrialModal from './LazyModuleTrialModal';
import DemoPlayerModal from '@/help/demos/LazyDemoPlayerModal';
import { useOrganizationStore } from '@/store/organizationStore';
import { useModuleStore } from '@/store/moduleStore';
import { useUIStore } from '@/store/uiStore';
import { useAuthStore } from '@/store/authStore';
import api from '@/services/api';
import { isBoqEditorPath } from '@/lib/projectTabs';

// T2908: pomoč vleče react-markdown (unified/micromark/mdast, ~110 kB gzip). Lupina je v
// vstopnem grafu, zato je stranska vrstica len-lena: kos se naloži ob prvem odprtju.
const HelpSidebar = lazy(() => import('./HelpSidebar'));
// T2908: enako testna plošča (TestPanel + parser Tests_TODO) — vpne se ob prvem odprtju.
const TestPanel = lazy(() => import('./TestPanel'));
// T3108: zaslon brez organizacije je redek — ne sodi v vstopni graf.
const NoOrganizationScreen = lazy(() => import('./NoOrganizationScreen'));

/**
 * T2456 — poti, ki delujejo tudi brez organizacije. Profil in sistemska
 * administracija organizacije ne potrebujeta; projekti pa zato, ker zunanji
 * sodelavec (`ExternalProjectAccess`) do deljenih projektov dostopa brez
 * članstva — zaslon brez organizacije nanje vodi.
 *
 * T2656: Navodila (`/docs`) ne potrebujejo organizacije in ostanejo v stranski
 * vrstici tudi brez nje, zato morajo delovati.
 */
const ROUTES_WITHOUT_ORGANIZATION = ['/profile', '/sysadmin', '/projects', '/docs'];

/** T2656: zaslon brez organizacije živi na nadzorni plošči. */
const NO_ORGANIZATION_HOME = '/dashboard';

/**
 * T2939: osebna zavihka Nastavitev (Varnost — T2816, Zasebnost — T2915) delujeta
 * brez organizacije. Sysadmin brez članstva mora na Varnosti vklopiti obvezno
 * 2FA (`/settings?tab=security&setup2fa=1`), sicer ne pride do Sysadmin. Ostali
 * zavihki `/settings` brez organizacije nimajo vsebine, zato ostanejo preusmerjeni.
 */
const SETTINGS_TABS_WITHOUT_ORGANIZATION = ['security', 'privacy'];

function worksWithoutOrganization(pathname: string, search = ''): boolean {
  if (pathname === '/settings') {
    return SETTINGS_TABS_WITHOUT_ORGANIZATION.includes(
      new URLSearchParams(search).get('tab') ?? '',
    );
  }
  return ROUTES_WITHOUT_ORGANIZATION.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}

export default function DashboardLayout() {
  const { t } = useTranslation();
  const { pathname, search } = useLocation();
  const { fetchOrganizations, currentOrganization, organizations, loaded } =
    useOrganizationStore();
  const { fetchModules } = useModuleStore();
  const { isHelpOpen, toggleHelp, isTestPanelOpen, toggleTestPanel, isZenMode } = useUIStore();
  // T2908: sidebar se naloži (in ostane vpet zaradi animacije zapiranja) šele po prvem odprtju.
  const [helpEverOpened, setHelpEverOpened] = useState(isHelpOpen);
  if (isHelpOpen && !helpEverOpened) setHelpEverOpened(true);
  const [testPanelEverOpened, setTestPanelEverOpened] = useState(isTestPanelOpen);
  if (isTestPanelOpen && !testPanelEverOpened) setTestPanelEverOpened(true);
  const { user } = useAuthStore();
  const [orgLoaded, setOrgLoaded] = useState(false);
  const [resendStatus, setResendStatus] = useState<'idle' | 'loading' | 'sent'>('idle');

  const showEmailBanner = user && !user.email_verified;

  const handleResend = async () => {
    if (!user?.email || resendStatus !== 'idle') return;
    setResendStatus('loading');
    try {
      await api.post('auth/email-verify/resend/', { email: user.email });
      setResendStatus('sent');
    } catch {
      setResendStatus('idle');
    }
  };

  useEffect(() => {
    fetchOrganizations().finally(() => setOrgLoaded(true));
  }, []); // Only fetch once on layout mount

  useEffect(() => {
    if (currentOrganization) {
      fetchModules(currentOrganization.slug);
    }
  }, [currentOrganization, fetchModules]);

  // T2456: uspešno naložen, a prazen seznam pomeni »ni članstva« (izstop,
  // odstranitev). Napaka nalaganja (npr. 403 ob zaklepu) tega ne pomeni —
  // zaklep prevzame OrganizationBlockedScreen.
  const showNoOrganization =
    loaded && organizations.length === 0 && !worksWithoutOrganization(pathname, search);

  if (!orgLoaded) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="h-10 w-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-body font-medium text-muted-foreground">{t('app.loading_organizations')}</p>
        </div>
      </div>
    );
  }

  return (
    // T2887: overflow-clip, ne overflow-hidden — `hidden` je še vedno programsko
    // drsen vsebnik; fokus absolutno postavljenega elementa (npr. `sr-only` radio)
    // ga je pomaknil za ~650 px in odrezal ves prikaz na vrh okna.
    <div className="flex flex-col h-[calc(100vh-var(--env-banner-h,0px))] mt-[var(--env-banner-h,0px)] bg-background overflow-clip relative">
      {/* Banner: email ni potrjen */}
      {showEmailBanner && (
        <div className="flex-shrink-0 bg-amber-50 border-b border-amber-200 px-4 py-2 flex items-center justify-between gap-4 text-body">
          <div className="flex items-center gap-2 text-amber-800">
            <svg className="h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            <span>{t('dashboard.email_unverified_banner', 'Vaš e-naslov še ni potrjen. Preverite nabiralnik.')}</span>
          </div>
          <button
            onClick={handleResend}
            disabled={resendStatus !== 'idle'}
            className="flex-shrink-0 text-dense font-medium text-amber-700 underline hover:text-amber-900 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {resendStatus === 'loading'
              ? t('dashboard.email_unverified_resending', 'Pošiljam...')
              : resendStatus === 'sent'
                ? t('dashboard.email_unverified_sent', '✓ E-poštno sporočilo poslano')
                : t('dashboard.email_unverified_resend', 'Pošlji znova')}
          </button>
        </div>
      )}
      {/* T2157: brez povezave so poizvedbe zadržane — povej zakaj ni podatkov. */}
      <OfflineBanner />
      {/* T3077: popis s spletne strani čaka na uvoz v nov projekt. */}
      <ImportHandoffBanner />
      <div className="flex flex-1 min-h-0 overflow-hidden">
        <Sidebar />
        <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
          {/* Zen Mode (T805): skrij globalni Topbar za maksimalen prostor editorja. */}
          {/* T2410 (3): v urejevalniku popisa je zgornja vrstica stisnjena v glavo
              projekta (`ProjectHeader compact` → `TopbarCompact`) — ena vrstica
              namesto dveh, ~100 px več za tabelo. */}
          {!isZenMode && !isBoqEditorPath(pathname) && <Topbar />}
          {/* T2180: spodnji odmik = višina piškotne pasice (0, ko je ni). */}
          <main className="flex-1 overflow-y-auto pb-[var(--cookie-banner-h,0px)]">
            {/* T2656: poti, ki brez organizacije nimajo smisla (Predloge, Ekipa,
                Naročnina, Nastavitve …), preusmerijo na nadzorno ploščo, ki
                pokaže zaslon brez organizacije — tako se z zaslonom ujema tudi
                označeni vstop v stranski vrstici. */}
            {/* T2883: strani so lene (React.lazy) — lupina (stranska vrstica, glava)
                ostane, med nalaganjem se zamenja samo vsebina. T3108: enako zaslon
                brez organizacije. */}
            <Suspense
              fallback={
                <div className="flex justify-center py-24" role="status" aria-live="polite">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--bp-accent)]"></div>
                </div>
              }
            >
              {showNoOrganization ? (
                pathname === NO_ORGANIZATION_HOME ? (
                  <NoOrganizationScreen />
                ) : (
                  <Navigate to={NO_ORGANIZATION_HOME} replace />
                )
              ) : (
                <Outlet />
              )}
            </Suspense>
          </main>
        </div>
      </div>
      
      {/* Help Sidebar */}
      {helpEverOpened && (
        <Suspense fallback={null}>
          <HelpSidebar isOpen={isHelpOpen} onClose={() => toggleHelp(false)} />
        </Suspense>
      )}

      {/* Test Panel (dev/testing only) */}
      {testPanelEverOpened && (
        <Suspense fallback={null}>
          <TestPanel isOpen={isTestPanelOpen} onClose={() => toggleTestPanel(false)} />
        </Suspense>
      )}
      
      <ModuleTrialModal />
      <DemoPlayerModal />

      {/* Backdrop for mobile or when sidebar is very wide - optional but adds polish */}
      {isHelpOpen && (
        <div 
          className="fixed inset-0 bg-slate-900/5 z-40 sm:hidden" 
          onClick={() => toggleHelp(false)}
        />
      )}
    </div>
  );
}
